import PropTypes from "prop-types";
import { Message, SendMessage, TitlePageInfo } from "../components";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearUnreadConversation } from "../../store/chats";
import { adminAPI } from "../../api/adminAPI";
import { AlertBox, Spinner } from "../../ui";

export const ChatLayout = ({ conversation, userCanReply = false, serviceId }) => {
  const dispatch = useDispatch();
  const { jwt, email } = useSelector((state) => state.auth);
  const [isLoading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const isFirstScroll = useRef(true);

  const getMessagesFromTwilio = async () => {
    if (isLoading) return;
    const messagesCount = await conversation.getMessagesCount();
    if (messagesCount === 0) return setLoading(true);
    const messagesTwilio = await conversation.getMessages(messagesCount);
    const mapped = messagesTwilio.items.map(({ state }) => ({
      body: state.body,
      author: state.author,
      index: state.index,
      date: state.timestamp,
    }));
    setMessages(mapped);
    setLoading(true);
  };

  const handleNewMessage = async (message) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        body: message.state.body,
        author: message.state.author,
        index: message.state.index,
        date: message.state.timestamp,
      },
    ]);
    await conversation.updateLastReadMessageIndex(message.state.index);
  };

  useEffect(() => {
    getMessagesFromTwilio();
    dispatch(clearUnreadConversation(conversation.sid));
    conversation.on("messageAdded", handleNewMessage);
    return () => {
      conversation.off("messageAdded", handleNewMessage);
    };
  }, []);

  useEffect(() => {
    if (isLoading && messages.length > 0) {
      const timer = setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: isFirstScroll.current ? "auto" : "smooth",
        });
        isFirstScroll.current = false;
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [messages, isLoading]);

  const sendMessage = async (message = "") => {
    if (!userCanReply) {
      await AlertBox({
        color: "error",
        messages: ["No tienes permisos para enviar mensajes"],
      });
      return;
    }
    await conversation.sendMessage(message);

    try {
      await adminAPI.post(
        `/chat/send-push-notification/${serviceId}`,
        { message },
        { headers: { "x-token": jwt } }
      );
    } catch (error) {
      console.error("Error sending push notification:", error);
    }
  };

  if (!isLoading) {
    return <Spinner />;
  }

  return (
    <div className="mb-20">
      <TitlePageInfo title="Chat" />
      {messages.map((message) => (
        <Message
          key={message.index}
          userName={message.author}
          message={message.body}
          date={message.date}
          isOwnMessage={message.author === email}
        />
      ))}
      <SendMessage sendMessage={sendMessage} />
    </div>
  );
};

ChatLayout.propTypes = {
  conversation: PropTypes.object.isRequired,
  userCanReply: PropTypes.bool.isRequired,
  serviceId: PropTypes.string.isRequired,
};
